// File utility functions for email attachments

/**
 * Convert a File to base64 string
 */
export async function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            const result = reader.result as string;
            if (!result) {
                reject(new Error('FileReader returned empty result'));
                return;
            }
            
            // Remove data URL prefix (e.g., "data:image/jpeg;base64,")
            const parts = result.split(',');
            if (parts.length < 2) {
                reject(new Error('Invalid data URL format - base64 data not found'));
                return;
            }
            
            let base64 = parts[1];
            if (!base64 || base64.length === 0) {
                reject(new Error('Base64 content is empty'));
                return;
            }
            
            // CRITICAL: Remove ALL whitespace (spaces, newlines, tabs)
            // Brevo silently rejects base64 with whitespace
            base64 = base64.replace(/\s/g, '');
            
            // Validate base64 format
            const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
            if (!base64Regex.test(base64)) {
                reject(new Error('Invalid base64 format'));
                return;
            }
            
            resolve(base64);
        };
        reader.onerror = (error) => {
            reject(error);
        };
    });
}

/**
 * Convert FileList to array of base64 attachments
 */
export async function fileListToAttachments(fileList: FileList | null): Promise<Array<{ name: string; content: string }>> {
    if (!fileList || fileList.length === 0) {
        return [];
    }

    const attachments = [];
    for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];
        try {
            const base64 = await fileToBase64(file);
            attachments.push({
                name: file.name,
                content: base64
            });
        } catch (error) {
        }
    }

    return attachments;
}

/**
 * Convert a single File to attachment format
 */
export async function fileToAttachment(file: File | null | undefined): Promise<Array<{ name: string; content: string }>> {
    if (!file) {
        return [];
    }


    try {
        const base64 = await fileToBase64(file);
        
        if (!base64 || base64.length === 0) {
            return [];
        }

        // Get content type from file, with fallback based on extension
        let contentType = file.type;
        if (!contentType || contentType === '') {
            const extension = file.name.split('.').pop()?.toLowerCase();
            const mimeTypes: Record<string, string> = {
                'pdf': 'application/pdf',
                'doc': 'application/msword',
                'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'jpg': 'image/jpeg',
                'jpeg': 'image/jpeg',
                'png': 'image/png'
            };
            contentType = mimeTypes[extension || ''] || 'application/octet-stream';
        }

        const attachment = {
            name: file.name,
            content: base64,
            contentType: contentType
        };

        return [attachment];
    } catch (error) {
        return [];
    }
}

